import { ROLE_BY_ID, SCOPE_LABEL } from '../roleTaxonomy';
import type { UserRow } from '../userViewModel';

import UserRegionChips from './UserRegionChips';
import UserRoleChips from './UserRoleChips';

interface UserDetailRoleCardProps {
  user: UserRow;
}

/** Role + region card inside the detail pane Profile tab. Renders one row
 *  per assigned role with its scope and full description. Editing is
 *  driven by the single "Edit role & regions" button in the pane footer —
 *  the per-card edit affordance was removed to avoid duplicate entry
 *  points to the same modal. */
export default function UserDetailRoleCard({ user }: UserDetailRoleCardProps) {
  const hasRoles = user.roles.length > 0;
  return (
    <div className="px-[18px] pb-3.5">
      <div className="text-[10px] font-semibold tracking-[.08em] uppercase text-stone-500 dark:text-stone-400 mb-2">
        Role &amp; access
      </div>
      <div className="p-3 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-[#1a1f1c]">
        <UserRoleChips roles={user.roles} max={5} />
        {!hasRoles && (
          <p className="text-xs text-stone-600 dark:text-stone-400 leading-snug mt-2">
            No Auth0 role assigned yet. Assign one to grant access.
          </p>
        )}
        {hasRoles && (
          <ul className="mt-2.5 space-y-2.5">
            {user.roles.map((roleId) => {
              const def = ROLE_BY_ID[roleId];
              if (!def) return null;
              return (
                <li key={roleId} className="text-[11.5px]">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-semibold text-bark-900 dark:text-[#e8ece9]">
                      {def.name}
                    </span>
                    <span className="text-[10px] text-stone-500 dark:text-stone-400 font-mono">
                      · {SCOPE_LABEL[def.scope]}
                    </span>
                  </div>
                  <p className="text-stone-600 dark:text-stone-400 leading-snug mt-0.5">
                    {def.desc}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
        <div className="mt-3 pt-2.5 border-t border-dashed border-stone-200 dark:border-stone-800">
          <div className="text-[10.5px] text-stone-500 dark:text-stone-400 mb-1.5">
            Regions{user.regions.length === 0 && ' (none — global role)'}
          </div>
          {user.regions.length > 0 && <UserRegionChips regions={user.regions} max={6} />}
        </div>
      </div>
    </div>
  );
}
